import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Check, ExternalLink, X } from "lucide-react";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";
import { Avatar } from "@fluentui/react-components";

import { AdminContract } from "@/queries/admin/contracts";

const SORTABLE_FIELDS = ["name"] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const ENTRIES_PER_PAGE = 10;

const ContractTable = ({
  contracts,
  //   setSelectedContract,
}: {
  contracts?: AdminContract[];
  //   setSelectedContract: (v: AdminContract | undefined) => void;
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("name");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const sortedEntries: AdminContract[] | undefined = useMemo(() => {
    if (!contracts) {
      return;
    }

    switch (sortField) {
      case "name":
        return [...contracts].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
    }
  }, [contracts, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedEntries) {
      return 1;
    }

    return Math.ceil(sortedEntries.length / ENTRIES_PER_PAGE);
  }, [sortedEntries]);

  const currentPageEntries = useMemo(
    () =>
      sortedEntries?.slice(
        (page - 1) * ENTRIES_PER_PAGE,
        page * ENTRIES_PER_PAGE,
      ),
    [sortedEntries, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("name")}
                  sortDirection={
                    sortField === "name" ? sortDirection : undefined
                  }
                >
                  Name
                </TableRowItem>
                <TableRowItem heading>Contract</TableRowItem>
                <TableRowItem heading>Job Opening</TableRowItem>
                <TableRowItem heading>Company</TableRowItem>
                <TableRowItem heading button>
                  Visible to Company
                </TableRowItem>
                <TableRowItem heading button>
                  Visible to Candidate
                </TableRowItem>
              </TableRow>
            </thead>
            <tbody>
              {currentPageEntries?.map((contract) => (
                <AdminRow key={`contract-${contract.id}`} contract={contract} />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const AdminRow = ({ contract }: { contract: AdminContract }) => {
  const navigate = useNavigate();

  return (
    <TableRow>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{contract.name}</span>
      </TableRowItem>
      <TableRowItem>
        <a
          href={contract.link}
          target="_blank"
          rel="noreferrer"
          className="flex gap-0.5 text-xs text-[#646cff] font-medium hover:underline"
        >
          Contract
          <ExternalLink size={16} />
        </a>
      </TableRowItem>
      <TableRowItem>
        {Boolean(contract.jobOpening) && (
          <div
            className="text-xs text-[#646cff] font-medium hover:underline cursor-pointer"
            onClick={() =>
              navigate(`/new-admin/job/${contract.jobOpening?.id}`)
            }
          >
            {contract.jobOpening?.role}
          </div>
        )}
      </TableRowItem>

      <TableRowItem>
        {Boolean(contract.jobOpening) && (
          <div
            className="text-xs text-[#646cff] font-medium hover:underline cursor-pointer"
            onClick={() =>
              navigate(`/new-admin/company/${contract.jobOpening?.company.id}`)
            }
          >
            <div className="flex flex-row gap-3 items-center">
              <Avatar
                image={{ src: contract.jobOpening?.company.logoLink }}
                name={contract.jobOpening?.company.name}
                size={24}
              />
              <span>{contract.jobOpening?.company.name}</span>
            </div>
          </div>
        )}
      </TableRowItem>
      <TableRowItem>
        {contract.showToCompany ? (
          <Check className="m-auto" size={16} stroke="#07574b" />
        ) : "showToCompany" in contract ? (
          <X className="m-auto" size={16} stroke="#d13a15" />
        ) : null}
      </TableRowItem>
      <TableRowItem>
        {contract.showToCandidate ? (
          <Check className="m-auto" size={16} stroke="#07574b" />
        ) : "showToCandidate" in contract ? (
          <X className="m-auto" size={16} stroke="#d13a15" />
        ) : null}
      </TableRowItem>
    </TableRow>
  );
};

export default ContractTable;
